import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";

import Pagination from "@components/layout/Pagination";
import Table from "@components/layout/Table";
import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import ConfirmModal from "@components/shared/ConfirmModal";
import Loading from "@components/shared/Loading";
import NewGeneralReportButton from "./NewGeneralReportButton";
import { useAppContext } from "@context/AppContext";
import { useReportSnapshots } from "@hooks/query/useReportSnapshots";
import { useDeleteReportSnapshot } from "@hooks/mutation/useDeleteReportSnapshot";
import { ReportSnapshot } from "@services/reportEngine";
import { formatReportGeneratedAt } from "./formatCell";
import { toastError, toastSuccess } from "@utils/toast";

/**
 * CR-SEQ-2026-025 §1.3.6 — saved snapshots. A snapshot whose template was
 * later deleted still lists, with the template marked.
 */
const ReportSnapshotsPanel: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { checkPermissions, loggedInInfo } = useAppContext();
  const currentUserId = loggedInInfo?.userDetails?.id;
  const deleteSnapshot = useDeleteReportSnapshot();
  const [deleting, setDeleting] = useState<ReportSnapshot | null>(null);

  // A snapshot's values never change, but keeping it is a retention call:
  // whoever generated it may remove it, and a Super User may remove any.
  const canManage = (snapshot: ReportSnapshot) =>
    checkPermissions(["GenerateReports"]) &&
    (checkPermissions(["SuperAdmin"]) ||
      snapshot.generatedByUserId === currentUserId);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteSnapshot.mutateAsync(deleting.id);
      toastSuccess(t("reporting.snapshot_deleted"));
    } catch {
      toastError(t("reporting.snapshot_delete_failed"));
    }
    setDeleting(null);
  };
  const [name, setName] = useState("");
  const [template, setTemplate] = useState("");
  const [generatedBy, setGeneratedBy] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // API pages from one; Pagination is zero-indexed (it renders index + 1).
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useReportSnapshots({
    name: name || undefined,
    template: template || undefined,
    generatedBy: generatedBy || undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    page,
    pageLimit: 20,
  });

  const rows = (data?.reportSnapshots ?? []).map(snapshot => ({
    id: snapshot.id,
    name: snapshot.name,
    template: snapshot.reportTemplate ? (
      <span
        className={
          snapshot.reportTemplate.isDeleted ? "italic text-gray-400" : ""
        }
      >
        {snapshot.reportTemplate.name}
        {snapshot.reportTemplate.isDeleted &&
          ` ${t("reporting.template_deleted_marker")}`}
      </span>
    ) : (
      t("reporting.ad_hoc_report")
    ),
    generatedBy: snapshot.generatedBy?.name ?? "-",
    generatedAt: formatReportGeneratedAt(snapshot.createdAt),
    action: (
      <Select
        placeholder={t("common.actions")}
        options={[
          {
            label: t("common.view"),
            value: "view",
            onSelect: () =>
              navigate(`/polestar/reports/adhoc/history/${snapshot.id}`),
          },
          ...(canManage(snapshot)
            ? [
                {
                  label: t("common.delete"),
                  value: "delete",
                  onSelect: () => setDeleting(snapshot),
                },
              ]
            : []),
        ]}
        onChange={(option: any) => option?.onSelect?.()}
        menuPortalTarget={document.body}
        isSearchable={false}
        value={null}
      />
    ),
  }));

  return (
    <div className="flex flex-col gap-4 grow p-4">
      <div className="flex flex-col gap-4">
        <div className="flex justify-end">
          <NewGeneralReportButton />
        </div>
        <form
          className="grid gap-4 md:grid-cols-2 2xl:grid-cols-5"
          onSubmit={event => event.preventDefault()}
        >
          <div>
            <Label htmlFor="snapshot-name">
              {t("reporting.search_snapshot_name")}
            </Label>
            <SearchInput
              id="snapshot-name"
              value={name}
              onChange={e => {
                setName(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <Label htmlFor="snapshot-template">
              {t("reporting.search_snapshot_template")}
            </Label>
            <SearchInput
              id="snapshot-template"
              value={template}
              onChange={e => {
                setTemplate(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <Label htmlFor="snapshot-generated-by">
              {t("reporting.search_snapshot_generated_by")}
            </Label>
            <SearchInput
              id="snapshot-generated-by"
              value={generatedBy}
              onChange={e => {
                setGeneratedBy(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="min-w-[10rem]">
            <Label htmlFor="snapshot-from-date">
              {t("reporting.snapshot_from_date")}
            </Label>
            <input
              id="snapshot-from-date"
              type="date"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={fromDate}
              max={toDate || undefined}
              onChange={e => {
                setFromDate(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="min-w-[10rem]">
            <Label htmlFor="snapshot-to-date">
              {t("reporting.snapshot_to_date")}
            </Label>
            <input
              id="snapshot-to-date"
              type="date"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={toDate}
              min={fromDate || undefined}
              onChange={e => {
                setToDate(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </form>
      </div>

      {isLoading ? (
        <Loading />
      ) : isError ? (
        <div className="text-sm text-red-700">
          <p>{t("reporting.history_load_failed")}</p>
          <button
            type="button"
            className="btn-default mt-3"
            onClick={() => refetch()}
          >
            {t("common.refresh")}
          </button>
        </div>
      ) : (
        <Table
          fields={[
            { title: t("common.name"), name: "name" },
            { title: t("reporting.snapshot_template"), name: "template" },
            {
              title: t("reporting.snapshot_generated_by"),
              name: "generatedBy",
            },
            {
              title: t("reporting.snapshot_generated_at"),
              name: "generatedAt",
            },
            { title: t("common.actions"), name: "action" },
          ]}
          data={rows}
          noRowsText={t(
            name || template || generatedBy || fromDate || toDate
              ? "reporting.no_snapshots_match"
              : "reporting.no_snapshots",
          )}
        />
      )}

      <ConfirmModal
        open={Boolean(deleting)}
        onConfirm={handleDelete}
        onClose={() => setDeleting(null)}
        confirmText={t("common.delete")}
        isSubmitting={deleteSnapshot.isLoading}
      >
        {t("reporting.delete_snapshot_body", { name: deleting?.name ?? "" })}
      </ConfirmModal>

      {data && (data.meta?.lastPage ?? 1) > 1 && (
        <Pagination
          pageNo={page - 1}
          lastPage={data.meta.lastPage}
          onPageChange={index => setPage(index + 1)}
        />
      )}
    </div>
  );
};

export default ReportSnapshotsPanel;
